import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

/**
 * Full-screen overlay chrome shared by tables and charts: fills the browser tab
 * (above the app nav), with a header + close button, Escape-to-close, and a
 * body-scroll lock. The body is a scroll container; callers can pass a ref and
 * onScroll (e.g. tables for lazy loading).
 */
export default function FullscreenOverlay({ title, onClose, bodyRef, onScroll, children }) {
  const { t } = useTranslation()
  useEffect(() => {
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  return (
    <div className="fd-fs" role="dialog" aria-modal="true" aria-label={title}>
      <div className="fd-fs__header">
        <span className="fd-mono" style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{title}</span>
        <button className="fd-btn fd-btn--ghost fd-btn--icon fd-btn--sm" aria-label={t('Sentinel.V2.table.exitFullScreen')} onClick={onClose}>
          <i className="fa-solid fa-xmark" aria-hidden="true" />
        </button>
      </div>
      <div className="fd-fs__body" ref={bodyRef} onScroll={onScroll}>
        {children}
      </div>
    </div>
  )
}

FullscreenOverlay.propTypes = {
  title: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  bodyRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  onScroll: PropTypes.func,
  children: PropTypes.node,
}
